import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStyles } from './ManageClientModal.styles';
import { RockyButton } from '@shared/components/RockyButton';
import { RockyModal } from '@shared/components/RockyModal';
import { Field, Form, Formik, FormikErrors, FormikProps } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { validationSchema } from '../constants/validationSchema';
import isEmpty from 'lodash/isEmpty';
import { AddClietContactData, ClietContactFormData } from '@modules/EvaluationModule/interfaces/ClietContactFormData';
import { useModalState } from '@shared/hooks/useModalState';
import { addClientContact, editClientContact, setClientSubProjectNetworkError } from '../../redux/actions';
import classNames from 'classnames';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ClientsPositions } from '@modules/EvaluationModule/enums/ClientsPositions';
import { ClientsTypeOfContacts } from '@modules/EvaluationModule/enums/ClientsTypeOfContact';
import { ClientRmContactStatus } from '@modules/EvaluationModule/enums/ClientRmContactStatus';
import { DataState } from '@shared/enums/DataState';
import { ClientContactData } from '@modules/EvaluationModule/interfaces/ClientSubProjectData';
import { ClassNameMap } from '@mui/styles';
import AddIcon from '@mui/icons-material/Add';
import { ClientContactFromInitialValues } from '../constants/ClientContactFromInitialValues';
import { ClientContactFormFields } from '@modules/EvaluationModule/pages/ClientContactPage/components/constants/ClientContactFormFields';
import { editClientDataStateSelector, networkErrorSelector } from '../../redux/selectors';
import { ClientProjectData } from '@modules/EvaluationModule/interfaces/ClientProjectData';
import {
  Autocomplete,
  Box,
  Typography,
  Stack,
  TableRow,
  TextField,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import { trackEvent } from '@modules/App/redux/user/actions';
import { ClientWorkstreamData } from '../../../../interfaces/ClientWorkstreamData';

const EditClientButtonTrigger: React.FC<{ openModal: () => void; styles: ClassNameMap; children: ReactNode }> = ({
  openModal,
  children,
  styles,
}) => (
  <TableRow className={styles.contactRow} onClick={openModal}>
    {children}
  </TableRow>
);

const AddClientButtonTrigger: React.FC<{ openModal: () => void; styles: ClassNameMap }> = ({ openModal, styles }) => (
  <RockyButton cancel small className={styles.addBtn} onClick={openModal}>
    <AddIcon />
    Add contact
  </RockyButton>
);

const PermissionsSelector: React.FC<{
  value?: string;
  onChange: (next: string) => void;
  label?: string;
  className?: string;
}> = ({ value = '', onChange, label = 'Client Dashboard permissions', className }) => {
  const permissions = ['P', 'F', 'H', 'T', 'N'] as const;
  const selected = useMemo(() => new Set(value.toUpperCase().match(/[PFHTN](?=:[RW])/g) || []), [value]);
  const toggle = (l: typeof permissions[number]) => {
    const next = new Set(selected);
    next.has(l) ? next.delete(l) : next.add(l);
    // Every grant this control writes is full access.
    onChange(
      permissions
        .filter((c) => next.has(c))
        .map((c) => `${c}:W`)
        .join(',')
    );
  };

  return (
    <Box className={className} sx={{ mt: 2 }}>
      <div>
        <span>{label}</span>
        <div>
          <Tooltip title="Enables access to My Teams page">
            <FormControlLabel
              control={<Checkbox checked={selected.has('T')} onChange={() => toggle('T')} />}
              label="My teams"
            />
          </Tooltip>
          <Tooltip title="Allows to view evaluations data">
            <FormControlLabel
              control={<Checkbox checked={selected.has('P')} onChange={() => toggle('P')} />}
              label="People"
            />
          </Tooltip>
          <Tooltip title="Allows to view bill rates and allocation">
            <FormControlLabel
              control={<Checkbox checked={selected.has('F')} onChange={() => toggle('F')} />}
              label="Financial"
            />
          </Tooltip>
          <Tooltip title="Enables acceess to hiring and candiates data">
            <FormControlLabel
              control={<Checkbox checked={selected.has('H')} onChange={() => toggle('H')} />}
              label="Hiring"
            />
          </Tooltip>
          <Tooltip title="Client can see NewHome and HotPipeline candidates">
            <FormControlLabel
              control={<Checkbox checked={selected.has('N')} onChange={() => toggle('N')} />}
              label="Hot pipeline"
            />
          </Tooltip>
        </div>
      </div>
    </Box>
  );
};

interface Props {
  contact?: ClientContactData;
  choosenProject: ClientProjectData;
  workstream?: ClientWorkstreamData;
  children?: ReactNode;
  /** The client's real workstreams (is_workstream: true) — options for the picker below. */
  workstreams: ClientWorkstreamData[];
}

export const ManageClientModal: React.FC<Props> = ({ contact, choosenProject, workstream, children, workstreams }) => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const networkError = useSelector(networkErrorSelector);
  const isEditMode = Boolean(contact);
  const editClientDataState = useSelector(editClientDataStateSelector);
  const formRef = useRef<FormikProps<ClietContactFormData>>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const { openModal, closeModal, isModalOpen } = useModalState();
  const [doNotDisturb, setDoNotDisturb] = useState<boolean>(true);
  const [isDirty, setIsDirty] = useState(false);

  const initiallyRegistered = !!contact?.clientDashboardUserId;
  const [shouldCreateDashboardUser, setShouldCreateDashboardUser] = useState(initiallyRegistered);

  const allowedForCD = useMemo(
    () => new Set<ClientRmContactStatus>([ClientRmContactStatus.mainCurrentPoc, ClientRmContactStatus.currentPoc]),
    []
  );

  // ROCKY-27763: titles are stored in Zoho as free text, and the write path collapses
  // `Other` + customTitle into that one string. Nothing expands it back on read, so a title
  // that is not one of the five ClientsPositions matches no <option> and the select renders
  // blank — while the main screen shows it. Expanding it here, rather than in
  // mapContactsFromResponse, keeps contact.title intact for the list and the Title filter.
  const isPresetTitle = (title?: string) => !!title && (Object.values(ClientsPositions) as string[]).includes(title);

  const initialValues = contact
    ? {
        ...contact,
        workstream: contact.workstream || [],
        accountName: choosenProject.name,
        ...(contact.title && !isPresetTitle(contact.title) ? { title: 'Other', customTitle: contact.title } : {}),
      }
    : { ...ClientContactFromInitialValues, accountName: choosenProject.name, workstream: [] };

  useEffect(() => {
    if (isModalOpen) setShouldCreateDashboardUser(initiallyRegistered);
  }, [isModalOpen, initiallyRegistered]);

  useEffect(() => {
    setDoNotDisturb(contact?.doNotTouch ? true : false);
  }, [contact]);

  const executeModalScroll = () => {
    if (modalRef.current) {
      modalRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleClickSubmitIfValid = (
    validateForm: (values?: ClietContactFormData) => Promise<FormikErrors<ClietContactFormData>>
  ) => {
    if (!formRef.current) return;
    validateForm().then((errors) => {
      if (isEmpty(errors)) {
        formRef.current?.handleSubmit();
      } else {
        setIsDirty(true);
        executeModalScroll();
      }
    });
  };

  useEffect(() => {
    if (editClientDataState === DataState.Fulfilled) {
      closeModal();
    }
  }, [editClientDataState, closeModal]);

  const closeEditModal = useCallback(() => {
    closeModal();
    dispatch(setClientSubProjectNetworkError(null));
  }, [closeModal, dispatch]);

  const openClientModal = useCallback(() => {
    openModal();
  }, [openModal]);

  const handleSubmitEditClient = useCallback(
    (values: ClietContactFormData) => {
      const newlyEnrolling = !initiallyRegistered && shouldCreateDashboardUser;
      if (newlyEnrolling) {
        dispatch(
          trackEvent({
            actionType: 'CLIENT_DASHBOARD_ENROLL',
            data: {},
          })
        );
      }

      if (contact && workstream) {
        dispatch(
          editClientContact({
            ...values,
            workstreamName: workstream.name,
            clientId: choosenProject.id,
            doNotTouch: doNotDisturb,
            id: contact.id,
            emailAeTool: values.emailAeTool.toLowerCase(),
            shouldCreateDashboardUser,
          })
        );
        return;
      }
      const selectedWorkstreams = workstreams.filter((ws) => (values.workstream || []).includes(ws.zoho_crm_id));
      const addClientContactData: AddClietContactData = {
        ...values,
        workstreamName: selectedWorkstreams[0]?.name ?? null,
        clientId: choosenProject.id,
        doNotTouch: doNotDisturb,
        emailAeTool: values.emailAeTool.toLowerCase(),
        zohoCrmId: choosenProject.zohoCrmId,
        shouldCreateDashboardUser,
      };
      dispatch(addClientContact(addClientContactData));
    },
    [
      contact,
      workstream,
      workstreams,
      dispatch,
      doNotDisturb,
      choosenProject,
      shouldCreateDashboardUser,
      initiallyRegistered,
    ]
  );

  return (
    <>
      {isEditMode && children ? (
        <EditClientButtonTrigger openModal={openClientModal} styles={styles} children={children} />
      ) : (
        <AddClientButtonTrigger openModal={openClientModal} styles={styles} />
      )}
      <RockyModal
        title="Client point of contact"
        isOpen={isModalOpen}
        onClose={closeModal}
        crossIcon
        modalBodyStyles={styles.modalBodyStyles}
        ref={modalRef}
        backdropClose={false}
      >
        <Formik
          enableReinitialize
          initialValues={initialValues!}
          validationSchema={validationSchema}
          innerRef={formRef}
          onSubmit={handleSubmitEditClient}
        >
          {({ validateForm, values, errors, setFieldValue }: FormikProps<ClietContactFormData>) => (
            <Form>
              <div className={styles.nameContainer}>
                <div className={styles.fieldContainer}>
                  <div>
                    <span className={styles.fieldTitle}>{ClientContactFormFields.firstName.label}</span>
                    {isDirty && errors.firstName && <span className={styles.fieldError}> {errors.firstName}</span>}
                  </div>
                  <Field name={ClientContactFormFields.firstName.name} className={styles.fieldsText} />
                </div>
                <div className={styles.fieldContainer}>
                  <div>
                    <span className={styles.fieldTitle}>{ClientContactFormFields.lastName.label}</span>
                    {isDirty && errors.lastName && <span className={styles.fieldError}> {errors.lastName}</span>}
                  </div>
                  <Field name={ClientContactFormFields.lastName.name} className={styles.fieldsText} />
                </div>
              </div>
              <div className={styles.fieldContainer}>
                <div>
                  <span className={styles.fieldTitle}>{ClientContactFormFields.title.label}</span>
                  {isDirty && errors.title && <span className={styles.fieldError}> {errors.title}</span>}
                </div>
                <div className={styles.selectContainer}>
                  <Field
                    name={ClientContactFormFields.title.name}
                    as="select"
                    className={classNames(styles.fieldsText, styles.select)}
                  >
                    <option style={{ display: 'none' }} value={''}>
                      -- Select title --
                    </option>
                    {Object.values(ClientsPositions).map((position, i) => (
                      <option key={i} value={position}>
                        {position}
                      </option>
                    ))}
                    <option value="Other">Other</option>
                  </Field>
                  <ExpandMoreIcon className={styles.arrow} />
                </div>
                {values.title === 'Other' && (
                  <div className={styles.fieldContainer}>
                    <div>
                      <span className={styles.fieldTitle}>{ClientContactFormFields.customTitle.label}</span>
                      {isDirty && errors.customTitle && (
                        <span className={styles.fieldError}> {errors.customTitle}</span>
                      )}
                    </div>
                    <Field name={ClientContactFormFields.customTitle.name} className={styles.fieldsText} />
                  </div>
                )}
                <label className={styles.checkboxLabel}>
                  <Field
                    type="checkbox"
                    name={ClientContactFormFields.technicalPerson.name}
                    className={styles.checkboxField}
                  />
                  <span>{ClientContactFormFields.technicalPerson.label} </span>
                </label>
                <div className={styles.fieldContainer}>
                  <div>
                    <span className={styles.fieldTitle}>{ClientContactFormFields.emailAeTool.label}</span>
                    {isDirty && errors.emailAeTool && <span className={styles.fieldError}> {errors.emailAeTool}</span>}
                  </div>
                  <Field name={ClientContactFormFields.emailAeTool.name} type="email" className={styles.fieldsText} />
                </div>
                <div className={styles.fieldContainer}>
                  <span className={styles.fieldTitle}>{ClientContactFormFields.rmTypeOfContact.label}</span>
                  <div className={styles.selectContainer}>
                    <Field
                      name={ClientContactFormFields.rmTypeOfContact.name}
                      as="select"
                      className={classNames(styles.fieldsText, styles.select)}
                    >
                      {Object.values(ClientsTypeOfContacts).map((contactType, i) => (
                        <option value={contactType} key={i}>
                          {contactType}
                        </option>
                      ))}
                    </Field>
                    <ExpandMoreIcon className={styles.arrow} />
                  </div>
                </div>
                <div className={styles.fieldContainer}>
                  <span className={styles.fieldTitle}>{ClientContactFormFields.rmContactStatus.label}</span>
                  <div className={styles.selectContainer}>
                    <Field
                      name={ClientContactFormFields.rmContactStatus.name}
                      as="select"
                      className={classNames(styles.fieldsText, styles.select)}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        const v = e.target.value as ClientRmContactStatus;
                        setFieldValue(ClientContactFormFields.rmContactStatus.name, v);
                        if (!allowedForCD.has(v)) setShouldCreateDashboardUser(false);
                      }}
                    >
                      {Object.values(ClientRmContactStatus).map((contactType, i) => (
                        <option value={contactType} key={i}>
                          {contactType}
                        </option>
                      ))}
                    </Field>
                    <ExpandMoreIcon className={styles.arrow} />
                  </div>
                  {initiallyRegistered && !allowedForCD.has(values.rmContactStatus as ClientRmContactStatus) && (
                    <Box className={styles.error} sx={{ mt: 1 }}>
                      Changing RM contact status to “{values.rmContactStatus}” will revoke this contact's Client
                      Dashboard access.
                    </Box>
                  )}
                </div>
                <div className={styles.fieldContainer}>
                  <>
                    <span className={styles.fieldTitle}>{ClientContactFormFields.location.label}</span>
                    {isDirty && errors.location && <span className={styles.fieldError}> {errors.location}</span>}
                  </>
                  <Field
                    name={ClientContactFormFields.location.name}
                    value={values.location || ''}
                    className={styles.fieldsText}
                  />
                </div>
                <div className={styles.fieldContainer}>
                  <div>
                    <span className={styles.fieldTitle}>{ClientContactFormFields.locationArea.label}</span>
                    {isDirty && errors.locationArea && (
                      <span className={styles.fieldError}> {errors.locationArea}</span>
                    )}
                  </div>
                  <Field name={ClientContactFormFields.locationArea.name} className={styles.fieldsText} />
                </div>

                <>
                  <div>
                    <span className={styles.fieldTitle}>{ClientContactFormFields.workstream.label}</span>
                    {isDirty && errors.workstream && <span className={styles.fieldError}> {errors.workstream}</span>}
                  </div>
                  <div className={styles.selectContainer}>
                    <Autocomplete
                      multiple
                      options={workstreams}
                      getOptionLabel={(opt) => opt.name}
                      value={workstreams.filter((ws) => (values.workstream || []).includes(ws.zoho_crm_id))}
                      onChange={(_, newValue) =>
                        setFieldValue(
                          ClientContactFormFields.workstream.name,
                          newValue.map((v) => v.zoho_crm_id)
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Workstreams"
                          placeholder="Select workstreams"
                        />
                      )}
                    />
                    {shouldCreateDashboardUser && (
                      <Typography variant="caption">
                        {(values.workstream?.length ?? 0) === 0
                          ? 'Client will have full account access in the Client Dashboard as no workstreams are selected.'
                          : 'Client will have access to selected workstreams in the Client Dashboard.'}
                      </Typography>
                    )}
                  </div>
                </>

                <Box sx={{ mt: 1 }}>
                  <label className={styles.checkboxLabel}>
                    <Field
                      type="checkbox"
                      name="shouldCreateDashboardUser"
                      checked={shouldCreateDashboardUser}
                      disabled={!allowedForCD.has(values.rmContactStatus as ClientRmContactStatus)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setShouldCreateDashboardUser(e.target.checked);
                      }}
                      className={styles.checkboxField}
                    />
                    Registered in client dashboard
                  </label>
                </Box>
                {!allowedForCD.has(values.rmContactStatus as ClientRmContactStatus) && (
                  <div className={styles.disclaimer}>
                    Only Main Current POC and Current POC contacts are allowed to be registered in client dashboard
                  </div>
                )}
                {shouldCreateDashboardUser && (
                  <PermissionsSelector
                    value={values.clientDashboardPermissions || ''}
                    onChange={(next) => setFieldValue('clientDashboardPermissions', next)}
                    className={styles.fieldContainer}
                  />
                )}
                {![ClientRmContactStatus.formerEmployee, ClientRmContactStatus.formerPoc].includes(
                  contact?.rmContactStatus as ClientRmContactStatus
                ) && (
                  <Stack rowGap="30px" sx={{ marginTop: '25px' }}>
                    <div>
                      <div>
                        <label className={styles.checkboxLabel}>
                          <Field
                            name={ClientContactFormFields.csatGiver.name}
                            type="checkbox"
                            className={styles.checkboxField}
                          />
                          {ClientContactFormFields.csatGiver.label}
                        </label>
                      </div>
                    </div>
                    <div>
                      <div>
                        <label className={styles.checkboxLabel}>
                          <Field
                            name={ClientContactFormFields.feedbackGiver.name}
                            type="checkbox"
                            className={styles.checkboxField}
                          />
                          {ClientContactFormFields.feedbackGiver.label}
                        </label>
                      </div>
                      <div className={styles.emailNotificationContainer}>
                        <label>
                          <Field
                            type="radio"
                            name={ClientContactFormFields.doNotTouch.name}
                            value={doNotDisturb}
                            checked={!doNotDisturb}
                            onChange={() => {
                              setDoNotDisturb(false);
                            }}
                            className={styles.emailRadio}
                          />
                          {ClientContactFormFields.doNotTouch.label}
                        </label>
                        <label>
                          <Field
                            type="radio"
                            name={ClientContactFormFields.doNotTouch.name}
                            value={doNotDisturb}
                            checked={doNotDisturb}
                            onChange={() => {
                              setDoNotDisturb(true);
                            }}
                            className={styles.emailRadio}
                          />
                          Send link manually
                        </label>
                      </div>
                      {isDirty && doNotDisturb === null && <span className={styles.fieldError}> please choose</span>}
                      <div className={styles.disclaimer}>
                        {!doNotDisturb
                          ? 'Client will receive an email when evaluation starts and will be reminded 3 times each Monday before the evaluation'
                          : 'Unique link will be generated for you to reach out to the client'}
                      </div>
                    </div>
                  </Stack>
                )}
              </div>
              {networkError && <div className={styles.error}>{networkError}</div>}
              <div className={styles.btnContainer}>
                <RockyButton onClick={closeEditModal}>Cancel</RockyButton>
                {editClientDataState === DataState.Pending ? (
                  <RockyButton action disabled>
                    <div className={styles.spinner}></div>
                  </RockyButton>
                ) : (
                  <RockyButton action onClick={() => handleClickSubmitIfValid(validateForm)}>
                    {isEditMode ? 'Save' : 'Add'}
                  </RockyButton>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </RockyModal>
    </>
  );
};
